// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import cstmTable from '../../../../share/components/vue/PaginationTable';
import config from '../../../../share/config';
import { wizardComponents } from './componentsMap';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import api from '../../../../common/api';
import alert from '../../../../share/alert';
import loading from '../../../../share/components/vue/ProcessingIcon';
import util from '../../../../share/util/utils';
import eventLogUtil from '../../../../share/util/eventLogUtil';
import constants from '../../../../common/nodejs/constants';

export default {
  name: "spWorkSupport",
  data() {
    return {
      processing: false,
      currentWizard: '',
      // APIから取得した機能リストを常に保持しておく
      originalFunctionList: [],
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      // 選択中の機能オブジェクト
      selectedFunction: {},
      // フィルター用テキスト
      filterText: '',
      // システム間移動判定
      isUseCache: true,
      // SPWS用のi18nKey
      i18nWsKey: '',
      menuResponse: null,
      params: null,
      previousName: '',
      queryName: ''
    };
  },

  props: {
    // noop
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    ...wizardComponents
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.menuResponse) {
          return;
        }
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const MenuList = this.getWsi18nMenuList(this.menuResponse, systemId);
        this.filterFunctionList = MenuList;
        this.originalFunctionList = MenuList;
        this.updateFilter();
      }

    },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId,
      isPopstateNavigation: state => state.cache.isPopstateNavigation,
    }),
    /**
     * テーブルのカラム名取得
     * @returns カラム名
     */
    systemTableColumns() {
      return [
        { name: 'menu_title', label: 'individual.header_work_support_table_item' },
      ];
    }
  },

  // 遷移してきた画面のパラメータ情報を保持
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      vm.beforeRouteProcess(vm, from);
    });
  },

  /**
   * 遷移してきた画面のパラメータ情報を保持
   * 同一コンポーネントが再帰的に利用された場合、呼び出される
   */
  beforeRouteUpdate(to, from, next) {
    if (this.isPopstateNavigation) {
      this.cacheIsPopstateNavigation(false);
      next();
      return;
    }
    this.$nextTick(() => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      this.beforeRouteProcess(this, from);
      // テーブル情報クリア（本当は mountedLogic に入れたいが、影響範囲を考慮してここにピンポイントで入れている）
      this.originalFunctionList = [];
      this.filterFunctionList = [];
      // mounted時に行う共通処理を呼び出す
      this.mountedLogic();
    });
    next();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),
    ...mapActions('cache', ['cacheIsPopstateNavigation']),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      this.updateTable();
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRow: function (selected) {
      this.selectedFunction = selected;
      this.setHighLight();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.filterFunctionList.forEach(item => {
        if (item.work_support_id === this.selectedFunction.work_support_id) {
          item.fieldSpec = {};
          item.fieldSpec.menu_title = config.LIST_ITEM_HIGHLIGHT;
        } else {
          if (item.fieldSpec) {
            delete item.fieldSpec;
          }
        }
      });
    },

    /**
     * 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする
     */
    updateFilter() {
      this.selectedFunction = {};
      this.setHighLight();
      this.filterFunctionList = this.originalFunctionList.filter(
        // 翻訳後の名称を大文字・小文字区別なくフィルターする
        item => item.menu_title.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },

    /** サポート機能選択時 */
    async onClickSelect() {
      // 選択中の機能がない場合、return
      if (!this.selectedFunction.menu_title) {
        return;
      }

      // システムIDの取得
      const systemId = individualDiagnose.getCurrentSystemId(this);
      // WS用のi18nKeyの取得
      this.i18nWsKey = `spws${systemId}.${this.selectedFunction.work_support_id}`;

      // イベントログの操作 ID 発行
      this.processing = true;
      await eventLogUtil.createEventLogHandleId(this.netAppId);
      this.processing = false;

      // requirement_id 名をコンポーネント名に変換する
      const componentName = `requirementSp${this.selectedFunction.requirement_id}`;
      this.currentWizard = componentName;

      // 特殊機能_作業サポート各機能の利用回数カウントアップ
      this.usageCountUp({ type: config.USAGE_COUNT_SP_WSP, systemName: systemId, immutableId: this.selectedFunction.work_support_id });

      // イベントログのページビューカウント
      eventLogUtil.eventLogPageViewCount(constants.EVENT_LOG_PAGE_VIEW_PAGE_NAME_SPECIAL_WSP);
    },

    /** ウィザードを閉じた後の処理 */
    closeWizard() {
      this.currentWizard = '';
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * テーブル情報の更新を行う
     */
    async updateTable() {
      try {
        this.processing = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        // 特殊機能の作業サポート一覧取得のポーリング処理を行う
        const response = await individualDiagnose.pollingApi(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await api.postCall(
              config.WSP,
              '/netApps/' + this.netAppId + '/systems/' + systemId + '/initialize',
              {
                use_cache: this.isUseCache,
                is_special: true
              }
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await api.getCall(
              config.WSP,
              '/netApps/' + this.netAppId + '/systems/' + systemId + '/initialize/requests/' + postApiResponse.data.request_id
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバック
          (postApiResponse) => {
            // リクエストID が正常に返ってきたら成功とする
            return postApiResponse && postApiResponse.data && postApiResponse.data.request_id ? '' : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
            if (getApiResponse && getApiResponse.data && getApiResponse.data.status) {
              switch (getApiResponse.data.status) {
                case config.STATUS_WSP_COMPLETED:
                  // ポーリング終了を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case config.STATUS_WSP_IN_PROGRESS:
                  // ポーリング続行を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case config.STATUS_WSP_FAILED:
                default:
                  // それ以外はエラー発生を返す
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合は、一覧データ取得処理を行う
          this.menuResponse = await this.getMenuResponse(this.netAppId, systemId);
          // responseが存在しない場合、処理終了
          if (!this.menuResponse) {
            return;
          }
          // 作業サポート一覧の多言語変換リストの取得
          const MenuList = this.getWsi18nMenuList(this.menuResponse, systemId);
          this.filterFunctionList = MenuList;
          this.originalFunctionList = MenuList;
          this.updateFilter();
          // 表示項目がない場合、非サポート画面を表示
          if (this.filterFunctionList.length < 1) {
            // OK押下時は何もしない
            alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
          }
        }
      } finally {
        this.processing = false;
      }
    },


    /**
     * 一覧取得処理を行う関数
     * @param {*} netAppId netAppId
     * @param {*} systemId systemId
     * @returns GetのResponse | null
     */
    async getMenuResponse(netAppId, systemId) {
      const query = { is_special: true };
      const response = await individualDiagnose.syncApi(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await api.getParamCall(
            config.WSP,
            '/netApps/' + netAppId + '/systems/' + systemId + '/menus',
            query
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          // special_menus が入っていれば成功とする
          return getApiResponse && getApiResponse.data && getApiResponse.data.special_menus ? '' : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
      // 処理失敗時は response が null になるので考慮
      return response ? response.data.special_menus : null;
    },

    /**
     * その他のエラー
     */
    showUnexpectedError() {
      alert.showError(this, null, this.$t('individual.error_Initialize_communication_error'), () => {});
    },

    /**
     * 作業サポートの多言語変換の一覧取得
     * @param {Array} menuResponse メニュー一覧
     * @param {string} systemId システム ID
     */
    getWsi18nMenuList(menuResponse, systemId) {
      /**
       * 翻訳キーとメッセージキーを元に文言が存在すれば、その翻訳後の文言を返す。
       * ない場合は、defaultMessage を返す（defaultMessage 未指定の場合は空文字を返す）
       * 機能要求ID: COM1のタイトルのみ、個別で判定を行う
       * @param {string} i18nWsMenuKey 翻訳キー
       * @param {string} messageKey メッセージキー
       * @param {number} work_support_id 作業サポートID
       * @param {string} special_requirement_id 機能要求ID
       * @returns {string | undefined} 翻訳後の文言（見つからなかった場合は undefined）
       */
      const convertI18nText = (i18nWsMenuKey, messageKey, work_support_id = 0, special_requirement_id = '') => {
        const key = `${i18nWsMenuKey}.${messageKey}`;
        // 翻訳キーとメッセージキーの組み合わせから文言が見つかった場合、翻訳語文言を返却する
        if (this.$te(key)) {
          return this.$t(key);
        }
        // 作業サポートIDと機能要求IDが環境変数に設定された"COM1"のIDである場合、COM1のタイトルを返却する
        if (messageKey === 'menu_title' && work_support_id === Number(process.env.VUE_APP_COM1_WORK_SUPPORT_ID) && special_requirement_id === process.env.VUE_APP_COM1_SPECIAL_REQUIREMENT_ID) {
          return this.$t('individual.menu_title_COM1');
        }
        // 見つからなかった場合、undefinedを返す
        return undefined;
      };

      // 多言語変換リストの取得
      return menuResponse.map(item => {
        const i18nWsMenuKey = `spws${systemId}.${item.work_support_id}`;
        return {
          work_support_id: item.work_support_id,
          sort_id: item.sort_id,
          requirement_id: item.special_requirement_id,
          // menu_title が見つからなかった場合は undefined のままにする
          menu_title: convertI18nText(i18nWsMenuKey, 'menu_title', item.work_support_id, item.special_requirement_id),
          // menu_description が見つからないケースは「説明なし」なので空にする
          // ※作業サポート一覧の仕様上、普通にあり得る
          menu_description: convertI18nText(i18nWsMenuKey, 'menu_description') || '',
          additional_info: item.additional_info,
        };
      }).filter(
        // Phase4.Step1 では暫定で、menu_title の有/無（翻訳の有/無）で判断する
        (item) => item.menu_title
      ).sort((a, b) => {
        // sort_id(表示順) 要素でソートする
        return a.sort_id - b.sort_id;
      });
    },

    /**
     * 呼び出し時のコンポーネント内ガードの共通処理
     * @param vm 呼び出し元のオブジェクト(this)
     * @param from 遷移元 
     */
    beforeRouteProcess(vm, from) {
      if (util.isFromSettingsPage(from.name) || util.isSameSystemPage(from)) {
        // 単位設定画面またはパスワード変更画面からの遷移時、または同じシステム内の子機能間への遷移の場合 trueを指定
        vm.isUseCache = true;
      } else {
        // 上記以外の場合、falseを指定
        vm.isUseCache = false;
      }
    }
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },
};
